import { FC, useEffect, useState } from 'react';
import { Button, Modal } from '@GxT-Team/gxt-components';

interface KeyValueModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (keyValuePairs: Record<string, string>) => void;
  keyLabels: string[];
}

const KeyValueModal: FC<KeyValueModalProps> = ({
  isOpen,
  handleClose,
  handleSubmit,
  keyLabels,
}) => {
  const [keyValuePairs, setKeyValuePairs] = useState<Record<string, string>>(
    keyLabels.reduce((acc, key) => ({ ...acc, [key]: 'NA' }), {}),
  );

  // Effect to auto-close and submit if no template variables
  useEffect(() => {
    if (isOpen && keyLabels.length === 0) {
      // If no template variables, automatically submit empty data and close
      handleSubmit({});
      handleClose();
    }
  }, [isOpen, keyLabels.length, handleSubmit, handleClose]);

  const handleInputChange = (key: string, value: string) => {
    setKeyValuePairs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSubmit = () => {
    handleSubmit(keyValuePairs);
    handleClose();
  };

  // If there are no template variables, don't render the modal at all
  if (keyLabels.length === 0) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="80rem" data-testid="key-value-modal">
      <Modal.ModalHeader>
        <Modal.ModalHeaderHeading>Template Parameters</Modal.ModalHeaderHeading>
      </Modal.ModalHeader>
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {keyLabels.map((key) => (
            <div key={key} className="flex flex-col">
              <label htmlFor={key} className="text-[#177ba6] font-medium text-lg mb-2">
                {key}
              </label>
              <input
                id={key}
                type="text"
                value={keyValuePairs[key]}
                placeholder={`Enter ${key}`}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-3 focus:outline-none focus:ring-1 focus:ring-[#177ba6]"
                data-testid={`key-value-input-${key}`}
              />
            </div>
          ))}
        </div>
      </div>
      <Modal.ModalFooter className="!justify-end mt-4 p-4">
        <Button variant="primary" onClick={onSubmit} data-testid="key-value-submit-btn">
          Add
        </Button>
        <Button variant="secondary" onClick={handleClose} data-testid="key-value-cancel-btn">
          Cancel
        </Button>
      </Modal.ModalFooter>
    </Modal>
  );
};

export default KeyValueModal;
